import { Observable, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { CLIENTS_MOCKUP } from '../../admin/services/clients-mockup.const';
import { Client } from '../../admin/model/client';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Role } from '../../admin/model/role.interface';
import { SortOrder } from '../../admin/components/utils/header/header.component';
import { UpdateUserRequest } from '../../admin/model/auth-requests';
import { User } from '../model/user';
import { environment } from 'src/environments/environment';
import { CurrentUserService } from './current-user.service';
import { AUTH_ROLES } from '../consts/auth-roles.consts';
import { RoleUtils } from 'src/app/utils/role.utils';
import { ApplicationsService } from '../../admin/services/applications.service';
import { Application } from '../../admin/model/application';
import { AuditLogEntry } from '../types/audit-log';

const HOSTNAME_O3 = environment.api.o3;

export interface UserResponse {
  total: number;
  users: Array<User>;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private currentUserService: CurrentUserService,
    private applicationsService: ApplicationsService
  ) {}

  downloadUserAccessData(): Observable<HttpResponse<Blob>> {
    return this.http.get(HOSTNAME_O3 + 'download', {
      ...this.apiService.getOptions(),
      responseType: 'blob',
      observe: 'response',
    });
  }

  delete(username: string): Observable<void> {
    const request = { username };
    return this.apiService.post(`user/disable`, request, HOSTNAME_O3);
  }

  trueDelete(username: string): Observable<void> {
    const request = { username };
    return this.apiService.post(`user/delete`, request, HOSTNAME_O3);
  }

  adminResetPassword(username: string) {
    const request = { username };
    return this.apiService.post(
      `user/admin-reset-password`,
      request,
      HOSTNAME_O3
    );
  }

  updateUser(
    account: string,
    email: string,
    username: string,
    groups: string[]
  ): Observable<User> {
    const request: UpdateUserRequest = {
      groups,
    };
    return this.apiService.post(
      'auth/admin/' + account + '/user/' + username,
      request,
      HOSTNAME_O3
    );
  }

  addUser(email: string, username: string, tenantId?: string): Observable<any> {
    const request: { username: string; email: string; tenantId?: string } = {
      username,
      email,
    };

    if (tenantId) {
      request.tenantId = tenantId;
    }

    return this.apiService.post('users', request, HOSTNAME_O3);
  }

  users(
    filter = '',
    limit = 50,
    page = 0,
    order: SortOrder = { column: 'username', order: 'desc' },
    application?: string
  ): Observable<UserResponse> {
    let url = 'users/list';
    if (application) {
      const params = new HttpParams().set('application', application);
      url = `users/list?${params.toString()}`;
    }

    return this.apiService.get<User[]>(url, HOSTNAME_O3).pipe(
      map((users) => {
        return {
          total: users.length,
          users,
        } as UserResponse;
      })
    );
  }

  user(username: string): Observable<User> {
    const params = new HttpParams().set('username', username);
    return this.apiService.get(`user?${params.toString()}`, HOSTNAME_O3);
  }

  addUserRole(username: string, roleId: string): Observable<Role[]> {
    const request = {
      username,
      group: roleId,
    };
    return this.apiService.post('user/add-role', request, HOSTNAME_O3);
  }

  addUserClient(username: string, clientId: string): Observable<Client[]> {
    const request = {
      username,
      tenantId: clientId,
    };
    // return of(CLIENTS_MOCKUP.clients);
    return this.apiService.post('user/assign-tenant', request, HOSTNAME_O3);
  }

  removeUserRole(username: string, roleId: string): Observable<Role[]> {
    const request = {
      username,
      group: roleId,
    };
    return this.apiService.post('user/remove-role', request, HOSTNAME_O3);
  }

  removeUserClient(username: string, clientId: string): Observable<Client[]> {
    const request = {
      username,
      tenantId: clientId,
    };
    // return of(CLIENTS_MOCKUP.clients.filter((c) => c.id !== clientId));
    return this.apiService.post('user/remove-tenant', request, HOSTNAME_O3);
  }

  getUsersForCurrentTenant(application?: string): Observable<UserResponse> {
    let url = 'users/list';
    if (application) {
      const params = new HttpParams().set('application', application);
      url = `users/list?${params.toString()}`;
    }

    return this.apiService.get<User[]>(url, HOSTNAME_O3).pipe(
      map((users) => {
        return {
          total: users.length,
          users,
        } as UserResponse;
      })
    );
  }

  canManageUser(targetUser: User): Observable<boolean> {
    return this.currentUserService.currentUser.pipe(
      map((currentUser) => {
        if (!currentUser) return false;

        if (currentUser.groups.includes(AUTH_ROLES.UM_ADMINISTRATOR))
          return true;

        const sacRoles = currentUser.groups.filter(
          (role) =>
            RoleUtils.isSACRole(role) && RoleUtils.isApplicationScopedRole(role)
        );
        if (sacRoles.length > 0) {
          const managedTenantId = RoleUtils.extractTenantIdFromRole(
            sacRoles[0]
          );
          const normalizedManagedTenantId = RoleUtils.normalizeTenantId(managedTenantId);
          const normalizedUserTenantIds = (targetUser.tenantIDs || []).map(id => RoleUtils.normalizeTenantId(id));
          return normalizedUserTenantIds.includes(normalizedManagedTenantId) || false;
        }

        return false;
      })
    );
  }

  isGlobalAdmin(): Observable<boolean> {
    return this.currentUserService.currentUser.pipe(
      map((currentUser) => {
        return (
          currentUser?.groups?.includes(AUTH_ROLES.UM_ADMINISTRATOR) || false
        );
      })
    );
  }

  isSAC(): Observable<boolean> {
    return this.currentUserService.currentUser.pipe(
      map((currentUser) => {
        return (
          currentUser?.groups?.some(
            (role) =>
              RoleUtils.isSACRole(role) &&
              RoleUtils.isApplicationScopedRole(role)
          ) || false
        );
      })
    );
  }

  getSACTenantId(): Observable<string | null> {
    return this.currentUserService.currentUser.pipe(
      map((currentUser) => {
        const sacRoles =
          currentUser?.groups?.filter(
            (role) =>
              RoleUtils.isSACRole(role) &&
              RoleUtils.isApplicationScopedRole(role)
          ) || [];
        const tenantId =
          sacRoles.length > 0
            ? RoleUtils.extractTenantIdFromRole(sacRoles[0])
            : null;
        return tenantId;
      })
    );
  }

  isUMAdministrator(): Observable<boolean> {
    return this.currentUserService.currentUser.pipe(
      map((currentUser) => {
        if (!currentUser) return false;
        return currentUser.groups.some(
          (group) => group.toUpperCase() === AUTH_ROLES.UM_ADMINISTRATOR
        );
      })
    );
  }

  getSACApplications(): Observable<string[]> {
    return this.currentUserService.currentUser.pipe(
      switchMap((currentUser) => {
        if (!currentUser) return of([]);

        return this.applicationsService.getApplications().pipe(
          map((applications: Application[]) => {
            if (currentUser.groups.includes(AUTH_ROLES.UM_ADMINISTRATOR)) {
              return applications.map((app) => app.applicationId);
            }

            const userApplications = new Set<string>();

            currentUser.groups.forEach((role) => {
              if (RoleUtils.isSACRole(role)) {
                const app = RoleUtils.extractApplicationFromRole(role);
                if (app) {
                  userApplications.add(app);
                }
              }
            });

            return applications
              .map((app) => app.applicationId)
              .filter((app) => userApplications.has(app));
          })
        );
      })
    );
  }

  getApplicationUsers(
    tenantId: string,
    application: string
  ): Observable<UserResponse> {
    const params = new HttpParams()
      .set('tenantId', tenantId)
      .set('application', application);

    return this.apiService
      .get<User[]>(`users/list?${params.toString()}`, HOSTNAME_O3)
      .pipe(
        map((users) => ({
          total: users.length,
          users,
        }))
      );
  }

  getAuditLogs(params?: {
    action?: string;
    username?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    tenantId?: string;
  }): Observable<AuditLogEntry[]> {
    let httpParams = new HttpParams();
    
    if (params) {
      if (params.action) {
        httpParams = httpParams.set('action', params.action);
      }
      if (params.username) {
        httpParams = httpParams.set('username', params.username);
      }
      if (params.startDate) {
        httpParams = httpParams.set('startDate', params.startDate);
      }
      if (params.endDate) {
        httpParams = httpParams.set('endDate', params.endDate);
      }
      if (params.limit) {
        httpParams = httpParams.set('limit', params.limit.toString());
      }
      if (params.tenantId) {
        httpParams = httpParams.set('tenantId', params.tenantId);
      }
    }

    const url = httpParams.toString() 
      ? `audit?${httpParams.toString()}`
      : 'audit';

    return this.apiService.get<AuditLogEntry[]>(url, HOSTNAME_O3);
  }
}
